import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useSimLaunchpad } from '@/hooks/useSimLaunchpad'
import { useSimPresales } from '@/hooks/useSimPresales'
import { isLaunchGraduated } from '@/lib/launchpad/curveMath'
import { tokenSharePath } from '@/lib/shareSlug'

interface TickerEntry {
  id: string
  kind: 'launch' | 'presale'
  ticker: string
  href: string
  raised: bigint
  rank: number
}

function formatTicker(ticker: string): string {
  const value = ticker.trim().replace(/^\$+/, '')
  return value ? `$${value}` : '$TOKEN'
}

function TickerItem({ item }: { item: TickerEntry }) {
  const isLaunch = item.kind === 'launch'
  const chip = isLaunch
    ? 'border-lime-400/25 bg-lime-400/10 text-lime-400'
    : 'border-orange-400/25 bg-orange-400/10 text-orange-400'

  return (
    <Link
      to={item.href}
      className="flex shrink-0 items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 transition-colors hover:border-white/20 hover:bg-white/[0.08]"
    >
      <span className="text-[11px] font-bold tabular-nums text-lime-400">#{item.rank}</span>
      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${chip}`}>
        {isLaunch ? 'Quick Launch' : 'Presale'}
      </span>
      <span className="text-sm font-semibold text-white">{formatTicker(item.ticker)}</span>
    </Link>
  )
}

function TrendingTicker() {
  const { launches } = useSimLaunchpad()
  const { presales } = useSimPresales()

  const trending = useMemo(() => {
    const items: TickerEntry[] = [
      ...launches.filter(launch => !isLaunchGraduated(launch)).map(launch => ({
        id: `launch-${launch.id}`,
        kind: 'launch' as const,
        ticker: launch.ticker,
        href: tokenSharePath(launch.shareSlug, launch.id),
        raised: launch.realQuoteRaised ?? 0n,
      })),
      ...presales.map(presale => ({
        id: `presale-${presale.id}`,
        kind: 'presale' as const,
        ticker: presale.ticker,
        href: tokenSharePath(presale.shareSlug, presale.id),
        raised: presale.raisedWei ?? 0n,
      })),
    ]

    return items
      .filter(item => item.ticker.trim())
      .sort((a, b) => {
        if (a.raised === b.raised)
          return a.ticker.localeCompare(b.ticker)
        return a.raised > b.raised ? -1 : 1
      })
      .slice(0, 10)
      .map((item, index) => ({ ...item, rank: index + 1 }))
  }, [launches, presales])

  if (trending.length === 0)
    return null

  const copies = trending.length < 8 ? 4 : 2

  return (
    <div className="relative z-10 border-b border-white/10 bg-black/70 backdrop-blur-xl">
      <div className="overflow-hidden px-3 py-2 sm:px-4">
        <div className="flex items-center gap-3">
          <span className="relative z-20 shrink-0 rounded-full bg-lime-400 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-black">
            Trending
          </span>
          <div className="relative min-w-0 flex-1 overflow-hidden" aria-label="Trending Quick Launches and Presales by ETH raised">
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-black to-transparent sm:w-12" />
            <div className="pp-marquee flex w-max items-center gap-2.5">
              {Array.from({ length: copies }, (_, copyIndex) => (
                <div key={copyIndex} className="flex items-center gap-2.5">
                  <span className="w-[48vw] shrink-0 sm:w-8" aria-hidden="true" />
                  {trending.map(item => (
                    <TickerItem key={`${item.id}-${copyIndex}`} item={item} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TrendingTicker
